import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CategoryIcon from "@mui/icons-material/Category";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import axios from "axios";
import { toast } from "react-toastify";

import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, toastError } from "../../utils/util";
import { getHeader } from "../../utils/utils2";
import useApiEp from "../../hooks/useApiEp";

const VisitorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);

  const { data, isLoading, isError, error } = useApiEp(`/user/get-visitor?id=${id}`);

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Container>
    );
  }

  const {
    _id,
    username,
    user_id,
    barcode_id,
    country,
    state,
    city,
    address,
    title,
    firstName,
    lastName,
    mobileNo,
    email,
    visitorGroup,
    companyName,
    industrySector,
    howDidYouFindOutAboutThisEvent,
    qrLink,
    createdOn,
    updatedOn,
    profile_image,
    profile_banner,
  } = data || {};

  const rawProductCats =
    data?.selectProductCategoriesChooseMin10Products ||
    data?.selectProductCategories ||
    data?.productCategories ||
    data?.selectedCategories ||
    data?.productCategory;

  const productCategories = Array.isArray(rawProductCats)
    ? rawProductCats
    : Array.isArray(rawProductCats?.value)
    ? rawProductCats.value
    : typeof rawProductCats === "string" && rawProductCats.trim()
    ? [rawProductCats]
    : [];

  const displayVal = (val) => (val !== null && val !== undefined && val !== "" ? val : "N/A");

  const fullName = `${title || ""} ${firstName || ""} ${lastName || ""}`.trim() || "N/A";
  const initial = firstName ? firstName[0].toUpperCase() : "V";

  const fullAddress = [address, city?.name, state?.name, country?.name].filter(Boolean).join(", ");

  return (
    <Box sx={{ pb: 8 }}>
      <Container maxWidth="lg">
        {/* Action Header Bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#475569",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 2,
              py: 0.75,
              backgroundColor: "#FFFFFF",
              border: "1px solid #CBD5E1",
              "&:hover": { backgroundColor: "#F1F5F9" },
            }}
          >
            Back to Directory
          </Button>

          <LoadingButton
            loading={resending}
            onClick={async () => {
              try {
                setResending(true);
                const { data: res } = await axios.post(
                  `${API_ENDPOINT}/admin/resend-email-by-a`,
                  { email: email },
                  getHeader(ADMIN)
                );
                toast.success(res?.message || "Badge email resent successfully!");
              } catch (err) {
                toastError(err);
              } finally {
                setResending(false);
              }
            }}
            variant="contained"
            startIcon={<MarkEmailReadIcon />}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 600,
              textTransform: "none",
              py: 0.75,
              px: 2.5,
              boxShadow: "0px 4px 12px rgba(37, 99, 235, 0.25)",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          >
            Resend Badge Email
          </LoadingButton>
        </Stack>

        {/* Profile Hero Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "20px",
            border: "1px solid #E2E8F0",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 6px 24px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
            mb: 3.5,
          }}
        >
          {profile_banner ? (
            <CardMedia component="img" height="200" image={profile_banner} alt={fullName} />
          ) : (
            <Box
              sx={{
                height: 180,
                background: "linear-gradient(135deg, #0F172A 0%, #2563EB 60%, #3B82F6 100%)",
              }}
            />
          )}

          <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 3, pt: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "center", sm: "flex-start" }}
              justifyContent="space-between"
              spacing={2.5}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "center", sm: "flex-start" }}
                spacing={2.5}
                flex={1}
              >
                <Avatar
                  src={profile_image || ""}
                  alt={fullName}
                  sx={{
                    width: 105,
                    height: 105,
                    mt: "-50px",
                    border: "4px solid #FFFFFF",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                    backgroundColor: "#EEF4FF",
                    color: "#2563EB",
                    fontSize: "2.4rem",
                    fontWeight: 800,
                  }}
                >
                  {initial}
                </Avatar>

                <Box flex={1} sx={{ pt: { xs: 0, sm: 1.5 }, textAlign: { xs: "center", sm: "left" } }}>
                  <Typography variant="h4" fontWeight="800" color="#1E293B" sx={{ letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                    {fullName}
                  </Typography>
                  {companyName && (
                    <Typography variant="body1" color="#475569" fontWeight="600" mt={0.5}>
                      Company: <span style={{ color: "#1E293B" }}>{companyName}</span>
                    </Typography>
                  )}
                </Box>
              </Stack>

              {visitorGroup && (
                <Chip
                  label={visitorGroup}
                  sx={{
                    mt: { xs: 0, sm: 1.5 },
                    backgroundColor: "#EEF4FF",
                    color: "#2563EB",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    py: 2.2,
                    px: 1.5,
                    borderRadius: "12px",
                    border: "1px solid #C7D9FE",
                  }}
                />
              )}
            </Stack>
          </Box>
        </Card>

        {/* Content Details Grid */}
        <Grid container spacing={3}>
          {/* Left Column: Personal Contact & Address */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              {/* Contact Information Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#EEF4FF",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Attendee Personal Details
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Email Address
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#2563EB" mt={0.25}>
                      {displayVal(email)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Mobile Number
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(mobileNo)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Username / Handle
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(username)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      User ID
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(user_id)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Address Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#F0FDF4",
                      color: "#16A34A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LocationOnIcon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Address & Location
                  </Typography>
                </Stack>

                <Typography variant="body1" color="#334155" fontWeight="500" sx={{ lineHeight: 1.6 }}>
                  {fullAddress || "N/A"}
                </Typography>

                {country?.code && (
                  <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                    <img
                      src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                      alt={`${country.name} flag`}
                      style={{ width: "26px", borderRadius: "3px" }}
                    />
                    <Typography variant="body2" fontWeight="600" color="#64748B">
                      {country.name}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Product Categories */}
              {productCategories && productCategories.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#FEF3C7",
                        color: "#D97706",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CategoryIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="700" color="#1E293B">
                      Interests & Product Categories
                    </Typography>
                  </Stack>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {productCategories.map((prod, idx) => {
                      let label = "";
                      if (typeof prod === "string") {
                        label = prod;
                      } else if (prod && typeof prod === "object") {
                        const parts = [
                          prod.MainCategory || prod.mainCat,
                          prod.Category || prod.subCat,
                          prod.SubCategory || prod.subSubCat,
                        ].filter(Boolean);
                        if (parts.length > 0) {
                          label = parts.join(" > ");
                        } else if (prod.code && prod.name) {
                          label = `${prod.code} - ${prod.name}`;
                        } else {
                          label =
                            prod.name ||
                            prod.label ||
                            prod.title ||
                            prod.code ||
                            JSON.stringify(prod);
                        }
                      }
                      return (
                        <Chip
                          key={idx}
                          label={label}
                          sx={{
                            backgroundColor: "#F8FAFC",
                            color: "#334155",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            border: "1px solid #E2E8F0",
                          }}
                        />
                      );
                    })}
                  </Box>
                </Paper>
              )}
            </Stack>
          </Grid>

          {/* Right Column: Registration & QR Code */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {/* Registration Meta Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Typography variant="h6" fontWeight="700" color="#1E293B" mb={2}>
                  Registration Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                      <Typography variant="caption" color="#64748B" fontWeight="600">
                        Barcode ID
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="800" color="#2563EB" mt={0.25}>
                        {displayVal(barcode_id)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Industry Sector
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(industrySector)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Event Discovery Source
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(howDidYouFindOutAboutThisEvent)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Created On
                    </Typography>
                    <Typography variant="body2" fontWeight="500" color="#475569" mt={0.25}>
                      {createdOn ? new Date(createdOn).toLocaleDateString() : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Updated On
                    </Typography>
                    <Typography variant="body2" fontWeight="500" color="#475569" mt={0.25}>
                      {updatedOn ? new Date(updatedOn).toLocaleDateString() : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Digital Pass QR Code Card */}
              {qrLink && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                    textAlign: "center",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
                    <QrCode2Icon sx={{ color: "#2563EB" }} />
                    <Typography variant="h6" fontWeight="700" color="#1E293B">
                      Digital Badge QR Code
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#F8FAFC",
                      borderRadius: "16px",
                      display: "inline-block",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <img
                      src={qrLink}
                      alt="Digital Badge QR Code"
                      style={{ width: "160px", height: "160px", borderRadius: "8px" }}
                    />
                  </Box>

                  <Typography variant="caption" color="#64748B" display="block" mt={1.5} fontWeight="500">
                    Scan badge at exhibition entry gates
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VisitorDetail;
